import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { ChevronDown, ChevronUp, Edit2, Trash2, Plus } from "lucide-react";
import Select from "react-select";
import { parseLocalDate } from "../../../utils/dateUtils";

/**
 * Set Reminder Section Component for Liabilities
 * Handles reminder management with Weekly/Monthly/Yearly types
 */
const SetReminderSection = ({
  showReminderSection,
  setShowReminderSection,
  reminderForm,
  setReminderForm,
  reminders,
  remindersCollapsed,
  setRemindersCollapsed,
  editingReminderId,
  handleAddReminder,
  handleEditReminder,
  handleDeleteReminder,
  handleCompleteReminder,
  getMonthName,
  userId,
  liabilityId,
  readOnly,
  showList = true
}) => {
  const { values, setFieldValue } = useFormikContext();
  const [showReminderForm, setShowReminderForm] = useState(!editingReminderId);

  // Default a new reminder to the day of the liability's next payment instead of day 1 (ML-19).
  const paymentDate = parseLocalDate(values?.nextPaymentDate || values?.paymentDueDate || values?.dueDate);
  const paymentDay = paymentDate ? String(paymentDate.getDate()) : null;

  useEffect(() => {
    setShowReminderForm(!!editingReminderId);
  }, [editingReminderId]);
  return (
    <div className="col-12 mb-4">
      <label className="form-label">Set Reminder</label>

      <div className="radio-group">
        <div className="form-check">
          <input
            type="radio"
            name="setReminderToggle"
            value="yes"
            className="form-check-input"
            id="reminderYes"
            checked={values ? values.setReminderToggle === "yes" : showReminderSection === "yes"}
            onChange={(e) => {
              if (setFieldValue) setFieldValue("setReminderToggle", e.target.value);
              setShowReminderSection("yes");
              if (paymentDay && !liabilityId && (!values.reminderDay || values.reminderDay === "1")) {
                setFieldValue("reminderDay", paymentDay);
              }
              if (paymentDay && liabilityId && !editingReminderId && !reminderForm.description) {
                setReminderForm({ ...reminderForm, dayOfMonth: paymentDay });
              }
            }}
            disabled={readOnly}
          />
          <label className="form-check-label" htmlFor="reminderYes">
            Yes
          </label>
        </div>
        <div className="form-check">
          <input
            type="radio"
            name="setReminderToggle"
            value="no"
            className="form-check-input"
            id="reminderNo"
            checked={values ? values.setReminderToggle === "no" : showReminderSection === "no"}
            onChange={(e) => {
              if (setFieldValue) setFieldValue("setReminderToggle", e.target.value);
              setShowReminderSection("no");
            }}
            disabled={readOnly}
          />
          <label className="form-check-label" htmlFor="reminderNo">
            No
          </label>
        </div>
      </div>

      {showReminderSection === "yes" && (
        <div className="reminder-form-container mt-3">
          {(showReminderForm || !liabilityId || (liabilityId && reminders.length === 0)) && (
            <div className="row">
              <div className="col-12 mb-3">
                <label className="form-label label-sm">Description*</label>
                <input
                  type="text"
                  className="form-control reminder-input"
                  placeholder="E.g. Monthly loan payment reminder"
                  value={liabilityId ? reminderForm.description : values.reminderDescription || ''}
                  onChange={(e) => {
                    if (liabilityId) {
                      setReminderForm({ ...reminderForm, description: e.target.value });
                    } else {
                      setFieldValue('reminderDescription', e.target.value);
                    }
                  }}
                  disabled={readOnly}
                />
              </div>
            <div
              className={`${
                (liabilityId ? reminderForm.type : values.reminderType || 'Monthly') === "Monthly"
                  ? "col-md-6"
                  : (liabilityId ? reminderForm.type : values.reminderType || 'Monthly') === "Weekly"
                  ? ""
                  : "col-md-4"
              } mb-3`}
            >
              <label className="form-label label-sm">Type*</label>
              <Select
                options={[
                  { value: "Weekly", label: "Weekly" },
                  { value: "Monthly", label: "Monthly" },
                  { value: "Yearly", label: "Yearly" }
                ]}
                value={{ value: liabilityId ? reminderForm.type : values.reminderType || 'Monthly', label: liabilityId ? reminderForm.type : values.reminderType || 'Monthly' }}
                onChange={(selectedOption) => {
                  if (liabilityId) {
                    setReminderForm({ ...reminderForm, type: selectedOption.value });
                  } else {
                    setFieldValue('reminderType', selectedOption.value);
                  }
                }}
                placeholder="Select reminder type"
                classNamePrefix="react-select"
                isSearchable={false}
                isDisabled={readOnly}
              />
            </div>

            {(liabilityId ? reminderForm.type : values.reminderType || 'Monthly') === "Monthly" ? (
              // 🔹 MONTHLY
              <div className="col-md-6 mb-3">
                <label className="form-label label-sm">Day of Month</label>
                <Select
                  options={[...Array(31)].map((_, i) => ({ value: i + 1, label: i + 1 }))}
                  value={{ value: liabilityId ? reminderForm.dayOfMonth : values.reminderDay || '1', label: liabilityId ? reminderForm.dayOfMonth : values.reminderDay || '1' }}
                  onChange={(selectedOption) => {
                    if (liabilityId) {
                      setReminderForm({
                        ...reminderForm,
                        dayOfMonth: selectedOption.value,
                      });
                    } else {
                      setFieldValue('reminderDay', selectedOption.value);
                    }
                  }}
                  classNamePrefix="react-select"
                  isSearchable={false}
                  isDisabled={readOnly}
                  placeholder="Select day"
                />
              </div>
            ) : reminderForm.type === "Weekly" ? (
              null
            ) : (
              // 🔹 YEARLY
              <>
                <div className="col-md-4 mb-3">
                  <label className="form-label label-sm">Month of Year</label>
                  <Select
                    options={[...Array(12)].map((_, i) => ({ value: i + 1, label: getMonthName(i + 1) }))}
                    value={{ value: liabilityId ? reminderForm.monthOfYear : values.reminderMonth || '1', label: getMonthName(liabilityId ? reminderForm.monthOfYear : values.reminderMonth || '1') }}
                    onChange={(selectedOption) => {
                      if (liabilityId) {
                        setReminderForm({
                          ...reminderForm,
                          monthOfYear: selectedOption.value,
                        });
                      } else {
                        setFieldValue('reminderMonth', selectedOption.value);
                      }
                    }}
                    classNamePrefix="react-select"
                    isSearchable={false}
                    isDisabled={readOnly}
                    placeholder="Select month"
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label label-sm">Day of Month</label>
                  <Select
                    options={[...Array(31)].map((_, i) => ({ value: i + 1, label: i + 1 }))}
                    value={{ value: liabilityId ? reminderForm.dayOfMonth : values.reminderDay || '1', label: liabilityId ? reminderForm.dayOfMonth : values.reminderDay || '1' }}
                    onChange={(selectedOption) => {
                      if (liabilityId) {
                        setReminderForm({
                          ...reminderForm,
                          dayOfMonth: selectedOption.value,
                        });
                      } else {
                        setFieldValue('reminderDay', selectedOption.value);
                      }
                    }}
                    classNamePrefix="react-select"
                    isSearchable={false}
                    isDisabled={readOnly}
                    placeholder="Select day"
                  />
                </div>
              </>
            )}

            {liabilityId && showList && (
              <div className="col-md-12 d-flex align-items-end mb-3 gap-2">
                <button
                  type="button"
                  className="btn btn-primary flex-grow-1"
                  onClick={handleAddReminder}
                  disabled={readOnly}
                >
                  {editingReminderId ? "Update Reminder" : "Add Reminder"}
                </button>
                {reminders.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowReminderForm(false)}
                    disabled={readOnly}
                  >
                    Back
                  </button>
                )}
              </div>
            )}
            </div>
          )}

          {reminders.length > 0 && !showReminderForm && showList && (
            <div className="reminders-list-section mt-2">
              <div
                className="reminders-collapse-header d-flex justify-content-between align-items-center"
                onClick={() => setRemindersCollapsed(!remindersCollapsed)}
              >
                <label className="form-label">Reminders</label>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowReminderForm(true);
                    }}
                  >
                    <Plus size={14} />
                  </button>
                  {remindersCollapsed ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronUp size={18} />
                  )}
                </div>
              </div>

              {!remindersCollapsed && (
                <div className="reminders-items-container mt-2">
                  {reminders.map((reminder, idx) => (
                    <div
                      key={reminder.id}
                      className="d-flex align-items-center bg-white py-2 px-3 rounded-md mb-2 last:mb-0"
                    >
                      <div className="w-6 h-6 d-inline-flex justify-center items-center rounded-full bg-blue-100 font-semibold flex-shrink-0 text-sm">
                        {idx + 1}
                      </div>
                      <div className="flex-grow-1 ms-2 pr-1">
                        <div className="font-semibold text-sm">
                          {reminder.description}
                        </div>
                        <div className="font-medium text-xs text-muted">
                          {reminder.type === "Weekly" || reminder.type === "weekly"
                            ? "Weekly"
                            : reminder.type === "Monthly" || reminder.type === "monthly"
                            ? `Monthly - Day ${reminder.dayOfMonth}`
                            : `Yearly - ${getMonthName(reminder.monthOfYear)} ${reminder.dayOfMonth}`}
                        </div>
                      </div>
                      {!readOnly && (
                        <div className="reminder-actions d-flex gap-3">
                          <button
                            type="button"
                            className="text-muted"
                            onClick={() => handleEditReminder(reminder)}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            type="button"
                            className="text-danger"
                            onClick={() => handleDeleteReminder(reminder.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SetReminderSection;
